import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Copy, Gift, Share2, Users } from "lucide-react";

/**
 * ReferralCard — invite friends, unlock bonus save tokens. Renders below
 * the Pro-tier card on the Streak page. Uses the Web Share API when
 * available, falls back to clipboard copy.
 */
export default function ReferralCard({ referrals }) {
  const { t } = useTranslation();
  const link = `${window.location.origin}/?ref=${referrals.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("streak_kids.referral_copied"));
    } catch {
      toast.error(t("common.toast_copy_failed"));
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t("streak_kids.referral_share_title"),
          text: t("streak_kids.referral_share_text"),
          url: link,
        });
      } else {
        copy();
      }
    } catch (e) {
      // AbortError = user cancelled the share sheet — expected
      if (e?.name !== "AbortError") console.error("Streak share failed:", e);
    }
  };

  const headlineParts = t("streak_kids.referral_headline", { max: referrals.max_bonus }).split(/<1>|<\/1>/);
  const bodyParts = t("streak_kids.referral_body", { count: referrals.referred_count }).split(/<1>|<\/1>/);
  const bonusKey = referrals.bonus_tokens === 1 ? "referral_bonus_suffix_one" : "referral_bonus_suffix_other";
  const bonusParts = referrals.bonus_tokens > 0
    ? t(`streak_kids.${bonusKey}`, { count: referrals.bonus_tokens }).split(/<1>|<\/1>/)
    : null;

  return (
    <div className="card-flat p-6 mt-6 animate-fadeup" data-testid="referral-card">
      <div className="grid md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={16} className="text-[var(--accent-primary)]"/>
            <div className="label-eyebrow">{t("streak_kids.referral_eyebrow")}</div>
          </div>
          <h3 className="text-xl font-medium tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>
            {headlineParts[0]}<span className="text-[var(--accent-primary)]">{headlineParts[1]}</span>{headlineParts[2]}
          </h3>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {bodyParts[0]}<span className="font-mono font-medium text-[var(--text-primary)]">{bodyParts[1]}</span>{bodyParts[2]}
            {bonusParts && (
              <>{bonusParts[0]}<span className="text-[var(--accent-positive)] font-medium">{bonusParts[1]}</span>{bonusParts[2]}</>
            )}.
          </p>
          <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-[var(--bg-accent)] border border-[var(--border-default)]">
            <Users size={14} className="text-[var(--text-muted)]"/>
            <input readOnly value={link} className="flex-1 bg-transparent text-sm font-mono text-[var(--text-secondary)] outline-none min-w-0 truncate" data-testid="referral-link-input"/>
            <button className="btn-ghost" onClick={copy} data-testid="referral-copy-btn"><Copy size={14}/></button>
            <button className="btn-primary" onClick={share} data-testid="referral-share-btn"><Share2 size={14}/> {t("streak_kids.share")}</button>
          </div>
        </div>
        <div className="flex md:justify-end">
          <div className="text-center">
            <div className="num-hero" data-testid="referral-count">{referrals.referred_count}</div>
            <div className="label-eyebrow mt-1">{t("streak_kids.friends_joined")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
